import axios, { AxiosError, AxiosResponse } from 'axios';
import { logger } from '../utils/logger';
import { transformToCamelCase } from '../utils/camelcase-transformer';
import type {
  AuthResponse,
  VerifyResponse,
  User,
  CarrierProfile,
  BASICScore,
  ViolationProjection,
  CrashProjection,
  HealthScore,
  EarlyWarning,
  Load,
  Credits,
  CarrierDashboard,
  BrokerDashboard,
  AdminDashboard,
  RiskAssessment,
} from '../types';
import type {
  ApiResponse,
  ApiResponseDataWithTotal,
  PaginatedResponse,
  SearchResult,
  Inspection,
  Violation,
  Crash,
  Vehicle,
  VinMatch,
  CensusData,
  Alert,
  MonitoredCarrier,
  SimpleHealthScore,
  FairScore,
  FairScoreResponse,
  SearchResponse,
  HistoricStats,
} from '../types/api';
import {
  isSearchResponse,
  isHistoricStats,
} from '../types/api';

// Create axios instance with base URL
// Use relative URL '/api' to leverage Vite proxy when VITE_API_BASE_URL is not set
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Rate limit error interface
interface RateLimitError {
  error: string;
  message: string;
  retryAfter: number;
  limit?: number;
  remaining?: number;
  resetTime?: string;
}

// Query timeout error interface (Plan 8)
interface QueryTimeoutError {
  error: string;
  message: string;
  statusCode: number;
  endpoint?: string;
  suggestions?: string[];
  recommendation?: string;
}

// Partial results interface (Plan 8)
interface PartialResultsResponse<T = unknown> {
  success: boolean;
  partial: boolean;
  data: T;
  meta?: {
    failedQueries?: string[];
    successfulQueries?: string[];
    suggestion?: string;
    queryTime?: string;
  };
}

// Response interceptor for error handling and unwrapping backend responses
api.interceptors.response.use(
  (response) => {
    // REMOVED camelCase transformation - caused data structure mismatches
    // Backend returns snake_case, frontend expects snake_case (original working state)
    // if (response.data) {
    //   response.data = transformToCamelCase(response.data);
    // }

    // Backend wraps responses in various formats - unwrap intelligently
    if (response.data && response.data.success !== undefined && response.data.data !== undefined) {
      // Plan 8: Preserve partial results metadata
      const isPartial = response.data.partial === true;
      const meta = response.data.meta;

      // Preserve pagination/list metadata (total, hasMore, count, limit, offset)
      const total = response.data.total;
      const hasMore = response.data.hasMore;
      const count = response.data.count;
      const limit = response.data.limit;
      const offset = response.data.offset;

      response.data = response.data.data;

      // Re-attach partial metadata if present
      if (isPartial) {
        response.data._partial = true;
        response.data._meta = meta;
      }

      // Re-attach pagination metadata if present (for lists with pagination)
      if (total !== undefined) {
        response.data = {
          data: response.data,
          total,
          hasMore,
          count,
          limit,
          offset
        };
      }

      // Handle nested content structures
      if (response.data.content) {
        // FMCSA carrier profile: content.carrier
        if (response.data.content.carrier) {
          response.data = response.data.content.carrier;
        }
        // BASIC scores: content (array)
        else if (Array.isArray(response.data.content)) {
          response.data = response.data.content;
          // Unwrap "basic" property if present in array elements
          if (response.data.length > 0 && response.data[0].basic) {
            response.data = response.data.map((item: { basic: unknown }) => item.basic);
          }
        }
      }
    }
    return response;
  },
  (error: AxiosError<RateLimitError | QueryTimeoutError>) => {
    // Plan 8: Handle 504 Gateway Timeout errors
    if (error.response?.status === 504) {
      const timeoutData = error.response.data as QueryTimeoutError;

      // Log timeout for debugging
      logger.warn('Query timeout', {
        endpoint: error.config?.url,
        message: timeoutData?.message,
        suggestions: timeoutData?.suggestions,
      });

      // Create a custom error with timeout info
      const timeoutError = new Error(
        timeoutData?.message || 'Query timed out. Try reducing the time range or adding filters.'
      ) as Error & {
        isTimeoutError: boolean;
        endpoint?: string;
        suggestions?: string[];
        recommendation?: string;
      };
      timeoutError.isTimeoutError = true;
      timeoutError.endpoint = timeoutData?.endpoint || error.config?.url;
      timeoutError.suggestions = timeoutData?.suggestions || [
        'Reduce the time range (months parameter)',
        'Add filters to narrow the query',
        'Try during off-peak hours',
      ];
      timeoutError.recommendation = timeoutData?.recommendation;

      // Dispatch a custom event for components that want to handle timeouts
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('queryTimeout', {
          detail: {
            endpoint: error.config?.url,
            message: timeoutError.message,
            suggestions: timeoutError.suggestions,
            recommendation: timeoutError.recommendation,
          }
        }));
      }

      return Promise.reject(timeoutError);
    }

    // Plan 8: Handle 503 Service Unavailable (circuit breaker)
    if (error.response?.status === 503) {
      const serviceData = error.response.data as { message?: string; details?: { resetTime?: string } };

      logger.warn('Service unavailable (circuit breaker)', {
        endpoint: error.config?.url,
        resetTime: serviceData?.details?.resetTime,
      });

      const circuitBreakerError = new Error(
        serviceData?.message || 'Service temporarily unavailable. Please try again in a few minutes.'
      ) as Error & {
        isCircuitBreakerError: boolean;
        resetTime?: string;
      };
      circuitBreakerError.isCircuitBreakerError = true;
      circuitBreakerError.resetTime = serviceData?.details?.resetTime;

      return Promise.reject(circuitBreakerError);
    }

    // Handle 429 Rate Limit errors
    if (error.response?.status === 429) {
      const rateLimitData = error.response.data as RateLimitError;
      const retryAfter = rateLimitData?.retryAfter || 60;

      // Log rate limit for debugging
      logger.warn('Rate limit exceeded', {
        endpoint: error.config?.url,
        retryAfter: `${retryAfter} seconds`,
        limit: rateLimitData?.limit,
        resetTime: rateLimitData?.resetTime,
      });

      // Show user-friendly notification
      // Using a simple alert as fallback, but you can integrate with your toast system
      const message = rateLimitData?.message ||
        `Rate limit exceeded. Please try again in ${retryAfter} seconds.`;

      // Create a custom error with additional rate limit info
      const rateLimitError = new Error(message) as Error & {
        isRateLimitError: boolean;
        retryAfter: number;
        resetTime?: string;
      };
      rateLimitError.isRateLimitError = true;
      rateLimitError.retryAfter = retryAfter;
      rateLimitError.resetTime = rateLimitData?.resetTime;

      // Dispatch a custom event for components that want to handle rate limits
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('rateLimitExceeded', {
          detail: {
            endpoint: error.config?.url,
            retryAfter,
            message,
            resetTime: rateLimitData?.resetTime,
          }
        }));
      }

      return Promise.reject(rateLimitError);
    }

    // Redirect to login on 401 errors (except for login/register endpoints)
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect if the request was for login or register
      if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authApi = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const { data } = await api.post('/auth/login', { email, password });
    // Check for error response (backend returns status: "error" on failure)
    if (data.status === 'error') {
      const error = new Error(data.message || 'Login failed') as Error & { response?: { data: typeof data } };
      error.response = { data };
      throw error;
    }
    // Interceptor unwraps { success, message, data } -> data
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    return data;
  },

  // Domain-gated self-signup (email confirmation flow)
  requestSignup: async (email: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post('/auth/signup/request', { email });
    return data;
  },

  getSignupInvite: async (token: string): Promise<{ email: string; companyName: string }> => {
    const { data } = await api.get(`/auth/signup/invite/${encodeURIComponent(token)}`);
    // Interceptor unwraps { success, data } -> data
    return data as { email: string; companyName: string };
  },

  completeSignup: async (token: string, fullName: string, password: string): Promise<AuthResponse> => {
    const { data } = await api.post('/auth/signup/complete', { token, fullName, password });
    // Interceptor unwraps { success, message, data } -> { token, user }
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    return data;
  },

  register: async (userData: {
    email: string;
    password: string;
    company_name: string;
    user_type: string;
    dot_number?: number;
    mc_number?: string;
  }): Promise<{ success: boolean; message: string; data: { id: number; email: string; status: string; submitted_at: string } }> => {
    const payload = {
      ...userData,
      role: userData.user_type,
    };
    const { data } = await api.post("/registration/submit", payload);
    return data;
  },

  verify: async (): Promise<VerifyResponse> => {
    const { data } = await api.get('/auth/verify');
    return data;
  },

  getProfile: async (): Promise<User> => {
    const { data } = await api.get('/auth/profile');
    return data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post('/auth/forgot-password', { email });
    return data;
  },

  verifyResetToken: async (token: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.get(`/auth/reset-password/${token}`);
    return data;
  },

  resetPassword: async (
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post(`/auth/reset-password/${token}`, {
      password,
      confirmPassword,
    });
    return data;
  },
};

// Result row from the location/advanced carrier search (snake_case from fmcsa_census)
export interface CarrierLocationRow {
  dot_number: number;
  legal_name: string;
  dba_name: string | null;
  phy_city: string | null;
  phy_state: string | null;
  phy_zip: string | null;
  power_units: number | null;
  truck_units: number | null;
  total_drivers: number | null;
  safety_rating: string | null;
  status_code: string | null;
  common_authority: string | null;
  contract_authority: string | null;
  broker_authority: string | null;
  phone: string | null;
  email_address: string | null;
}

export interface CarrierLocationResponse {
  results: CarrierLocationRow[];
  total: number;
  page: number;
  totalPages: number;
}

// A carrier that has hauled for a given shipper (from the shipper carrier-network endpoint)
export interface ShipperHauledCarrier {
  dotNumber: number;
  carrierName: string;
  inspectionCount: number;
  violationCount: number;
  oosCount: number;
  firstHauled: string | null;
  lastHauled: string | null;
  safetyRating: string | null;
  statusCode: string | null;
  phyCity: string | null;
  phyState: string | null;
  powerUnits: number | null;
  totalDrivers: number | null;
  phone: string | null;
  commonAuthority: string | null;
  contractAuthority: string | null;
  brokerAuthority: string | null;
}

// Carrier APIs
export const carrierApi = {
  // Find all carriers in a given city/state (location-first browse). Uses the
  // advanced-search branch of /carriers/search, which does not require a `q`.
  searchByLocation: async (params: {
    state?: string;
    city?: string;
    page?: number;
    limit?: number;
    safetyRating?: string;
    fleetSizeMin?: number;
    fleetSizeMax?: number;
    authorityStatus?: string;
    authorityType?: string; // 'carrier' | 'broker' | 'dual'
  }): Promise<CarrierLocationResponse> => {
    const qs = new URLSearchParams();
    if (params.state) qs.append('state', params.state);
    if (params.city) qs.append('city', params.city);
    qs.append('page', String(params.page ?? 1));
    qs.append('limit', String(params.limit ?? 50));
    if (params.safetyRating) qs.append('safetyRating', params.safetyRating);
    if (params.fleetSizeMin != null) qs.append('fleetSizeMin', String(params.fleetSizeMin));
    if (params.fleetSizeMax != null) qs.append('fleetSizeMax', String(params.fleetSizeMax));
    if (params.authorityStatus) qs.append('authorityStatus', params.authorityStatus);
    if (params.authorityType) qs.append('authorityType', params.authorityType);

    const { data } = await api.get(`/carriers/search?${qs.toString()}`);
    // Backend: { success, data: { results, total, page, totalPages } }; axios unwraps to data.
    const payload = ((data as any)?.data ?? data) as CarrierLocationResponse;
    return {
      results: payload?.results ?? [],
      total: payload?.total ?? 0,
      page: payload?.page ?? 1,
      totalPages: payload?.totalPages ?? 0,
    };
  },

  // All carriers that have hauled for a shipper (keyword-matched, paginated).
  getCarriersForShipper: async (shipperName: string, page = 1, limit = 50): Promise<{ results: ShipperHauledCarrier[]; total: number; page: number; totalPages: number }> => {
    const offset = (page - 1) * limit;
    const { data } = await api.get(`/shippers/${encodeURIComponent(shipperName)}/all-carriers?limit=${limit}&offset=${offset}`);
    // Interceptor re-shapes paginated responses to { data, total, hasMore, ... }
    const rows = (data?.data ?? data ?? []) as ShipperHauledCarrier[];
    const total = (data?.total ?? 0) as number;
    return { results: Array.isArray(rows) ? rows : [], total, page, totalPages: Math.ceil(total / limit) };
  },

  exportCarriersForShipper: async (shipperName: string): Promise<Blob> => {
    const res = await api.get(`/shippers/${encodeURIComponent(shipperName)}/all-carriers/export`, { responseType: 'blob' });
    return res.data as Blob;
  },

  // Full CSV export of all carriers matching a location filter. Streamed by the
  // backend (no row cap); fetched here as a blob in one authenticated request.
  exportByLocation: async (params: {
    state?: string;
    city?: string;
    safetyRating?: string;
    authorityStatus?: string;
    authorityType?: string;
  }): Promise<Blob> => {
    const qs = new URLSearchParams();
    if (params.state) qs.append('state', params.state);
    if (params.city) qs.append('city', params.city);
    if (params.safetyRating) qs.append('safetyRating', params.safetyRating);
    if (params.authorityStatus) qs.append('authorityStatus', params.authorityStatus);
    if (params.authorityType) qs.append('authorityType', params.authorityType);
    const res = await api.get(`/carriers/by-location/export?${qs.toString()}`, { responseType: 'blob' });
    return res.data as Blob;
  },

  search: async (query: string, type: string = 'name'): Promise<SearchResult[]> => {
    const { data } = await api.get<ApiResponse<SearchResponse>>(`/search/carriers?q=${encodeURIComponent(query)}&type=${type}`);
    // Use type guard to validate response structure
    if (isSearchResponse(data)) {
      return data.results;
    }
    // Fallback for backward compatibility
    if (Array.isArray(data)) {
      return data as SearchResult[];
    }
    logger.warn('Unexpected search response format:', data);
    return [];
  },

  getProfile: async (dotNumber: number): Promise<CarrierProfile> => {
    const { data } = await api.get(`/carriers/${dotNumber}`);
    return data;
  },

  getBASICScores: async (dotNumber: number): Promise<BASICScore[]> => {
    const { data } = await api.get(`/carriers/${dotNumber}/basics`);
    // Backend returns { success, data: [...BASICScore array] }
    // Interceptor unwraps to just the array
    return Array.isArray(data) ? data : [];
  },

  reportLoad: async (loadData: {
    loadNumber: string;
    shipperName: string;
    commodity: string;
    weight: number;
    miles: number;
    vehicleVin?: string;
    pickupDate: string;
    deliveryDate: string;
  }): Promise<{ success: boolean; load: Load; credits: Credits }> => {
    const { data } = await api.post('/carrier/loads', loadData);
    return data;
  },

  reportAccident: async (accidentData: Record<string, unknown>) => {
    const { data } = await api.post<ApiResponse<{ success: boolean }>>('/carrier/accidents', accidentData);
    return data;
  },

  reportBreakdown: async (breakdownData: Record<string, unknown>) => {
    const { data } = await api.post<ApiResponse<{ success: boolean }>>('/carrier/breakdowns', breakdownData);
    return data;
  },

  getCredits: async (): Promise<Credits> => {
    const { data } = await api.get('/carrier/credits');
    return data;
  },

  getLoads: async (): Promise<Load[]> => {
    const { data } = await api.get('/carrier/loads');
    return data.loads || [];
  },

  getInspections: async (dotNumber: number, limit: number = 100): Promise<ApiResponseDataWithTotal<Inspection>> => {
    const response = await api.get<ApiResponse<Inspection[]>>(`/carriers/${dotNumber}/inspections?limit=${limit}`);
    logger.debug('Inspections raw response', { dotNumber, limit, responseType: typeof response.data, hasData: 'data' in (response.data || {}), hasTotal: 'total' in (response.data || {}) });

    // After interceptor, response.data should be { data: Inspection[], total: number, hasMore: boolean, ... }
    // Check if we have the expected structure with pagination metadata
    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'total' in response.data) {
      return response.data as unknown as ApiResponseDataWithTotal<Inspection>;
    }

    // Fallback: if response.data is just an array (shouldn't happen after interceptor fix)
    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        total: response.data.length,
        hasMore: false
      };
    }

    // Last resort: return empty result
    return {
      data: [],
      total: 0,
      hasMore: false
    };
  },

  getCrashes: async (dotNumber: number, limit: number = 100): Promise<Crash[]> => {
    const response = await api.get<ApiResponse<Crash[]>>(`/carriers/${dotNumber}/crashes?limit=${limit}`);
    logger.debug('Crashes raw response', { dotNumber, limit, dataKeys: Object.keys(response.data || {}) });
    // Backend returns { success: true, data: [...crashes], count, ... }
    // Check if response.data.data exists (wrapped response) or if response.data is array (direct response)
    if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  getViolations: async (dotNumber: number, limit: number = 601): Promise<Violation[]> => {
    const response = await api.get<ApiResponse<Violation[]>>(`/carriers/${dotNumber}/violations?limit=${limit}`);
    logger.debug('Violations raw response', { dotNumber, limit, dataKeys: Object.keys(response.data || {}) });
    // Violations come in format: { success, data: [...violations], total, count }
    // Check if response.data.data exists (wrapped response) or if response.data is array (direct response)
    if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  getViolationsSummary: async (dotNumber: number): Promise<{
    total: number;
    bySeverity: { critical: number; high: number; medium: number; low: number };
    oosCount: number;
    byCategory: Array<{ category: string; count: number; oosCount: number }>;
  }> => {
    const response = await api.get<ApiResponse<{
      total: number;
      bySeverity: { critical: number; high: number; medium: number; low: number };
      oosCount: number;
      byCategory: Array<{ category: string; count: number; oosCount: number }>;
    }>>(`/carriers/${dotNumber}/violations/summary`);
    logger.debug("Violations summary raw response", { dotNumber, data: response.data });
    if (response.data && typeof response.data === "object" && "data" in response.data) {
      return (response.data as any).data;
    }
    return response.data as any;
  },

  getVehicles: async (dotNumber: number): Promise<Vehicle[]> => {
    const response = await api.get<ApiResponse<{ vehicles: Vehicle[] }>>(`/carriers/${dotNumber}/vehicles`);
    logger.debug('Vehicles raw response', { dotNumber, dataKeys: Object.keys(response.data || {}) });
    // Interceptor already unwrapped response.data.data -> response.data
    // So response.data is now {dotNumber, vehicleCount, vehicles}
    return (response.data as { vehicles?: Vehicle[] }).vehicles || [];
  },

  getCensusData: async (dotNumber: number): Promise<CensusData | null> => {
    const response = await api.get<CensusData | null>(`/carriers/${dotNumber}/census`);
    logger.debug('Census data raw response', { dotNumber, dataKeys: Object.keys(response.data || {}) });
    return response.data || null;
  },

  getObservedUnits: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const response = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/observed-units`);
    logger.debug('Observed units raw response', { dotNumber, dataKeys: Object.keys(response.data || {}) });
    return response.data || {};
  },

  getVINTimeline: async (vin: string): Promise<Record<string, unknown>> => {
    const response = await api.get<Record<string, unknown>>(`/vins/${vin}/timeline`);
    logger.debug('VIN timeline raw response', { vin, dataKeys: Object.keys(response.data || {}) });
    return response.data || {};
  },

  checkShellGame: async (vins: string[]): Promise<VinMatch[]> => {
    const response = await api.post<ApiResponse<VinMatch[]>>('/vins/check-shell-game', { vins });
    logger.debug('Shell game check raw response', { vinCount: vins.length, dataKeys: Object.keys(response.data || {}) });
    return Array.isArray(response.data) ? response.data : [];
  },

  getContactMatches: async (dotNumber: number): Promise<Record<string, unknown>> => {
    logger.debug('Fetching contact matches', { dotNumber });
    const response = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/contact-matches`);
    logger.debug('Contact matches raw response', { dotNumber, dataKeys: Object.keys(response.data || {}) });
    return response.data || {};
  },

  getChangeHistory: async (dotNumber: number): Promise<Record<string, unknown>> => {
    logger.debug('Fetching change history', { dotNumber });
    const response = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/change-history`);
    logger.debug('Change history raw response', { dotNumber, dataKeys: Object.keys(response.data || {}) });
    return response.data || {};
  },

  getHistoricStats: async (dotNumber: number): Promise<Record<string, unknown>> => {
    logger.debug('Fetching historic stats', { dotNumber });
    const response = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/historic-stats`);
    logger.debug('Historic stats raw response', { dotNumber, dataKeys: Object.keys(response.data || {}) });
    return response.data || {};
  },

  // VIN search - get carriers that have used this VIN
  getCarriersByVIN: async (vin: string): Promise<VinMatch> => {
    logger.debug('Fetching carriers for VIN', { vin });
    const response = await api.get<VinMatch>(`/vins/${vin}/carriers`);
    logger.debug('Carriers by VIN raw response', { vin, dataKeys: Object.keys(response.data || {}) });
    return response.data;
  },

  // Bayesian Fair Score - bias-corrected safety assessments
  getFairScores: async (dotNumber: number): Promise<FairScoreResponse> => {
    logger.debug('Fetching Bayesian fair scores', { dotNumber });
    const response = await api.get<FairScoreResponse>(`/carriers/${dotNumber}/fair-score`);
    logger.debug('Fair scores raw response', { dotNumber, dataKeys: Object.keys(response.data || {}) });
    return response.data;
  },

  getFairScoreByBasic: async (dotNumber: number, basicId: string): Promise<FairScore> => {
    logger.debug('Fetching Bayesian fair score by BASIC', { dotNumber, basicId });
    const response = await api.get<FairScore>(`/carriers/${dotNumber}/fair-score/${basicId}`);
    logger.debug('Fair score by BASIC raw response', { dotNumber, basicId, dataKeys: Object.keys(response.data || {}) });
    return response.data;
  },

  // Risk Score
  getRiskScore: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/risk-score`);
    return data || {};
  },

  // Fraud Indicators  
  getFraudIndicators: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/fraud-indicators`);
    return data || {};
  },
};

// Projection APIs
export const projectionApi = {
  getViolationProjections: async (dotNumber: number): Promise<ViolationProjection> => {
    const { data } = await api.get(`/projections/${dotNumber}/violations`);
    return data;
  },

  getCrashProjections: async (dotNumber: number): Promise<CrashProjection> => {
    const { data } = await api.get(`/projections/${dotNumber}/crashes`);
    return data;
  },

  /**
   * @deprecated Use scoringApi.getHealthScore() instead.
   * This endpoint returns the old CompanyHealthScore format.
   * The canonical endpoint is /api/scoring/health/:dotNumber which provides:
   * - Simple scores matching frontend calculation (with ?simple=true)
   * - Full CompanyHealthScore with components (without ?simple)
   * - Caching and historical tracking
   * @see scoringApi.getHealthScore
   */
  getHealthScore: async (dotNumber: number): Promise<HealthScore> => {
    console.warn('projectionApi.getHealthScore() is deprecated. Use scoringApi.getHealthScore() instead.');
    const { data } = await api.get(`/projections/${dotNumber}/health-score`);
    return data;
  },
};

// Alert APIs
export const alertApi = {
  getEarlyWarnings: async (dotNumber: number): Promise<EarlyWarning[]> => {
    const { data } = await api.get(`/alerts/early-warnings/${dotNumber}`);
    return data.alerts || [];
  },
};

// Dashboard APIs
export const dashboardApi = {
  getCarrierDashboard: async (): Promise<CarrierDashboard> => {
    const { data } = await api.get('/dashboard/carrier');
    return data;
  },

  getBrokerDashboard: async (): Promise<BrokerDashboard> => {
    const { data } = await api.get('/dashboard/broker');
    return data;
  },

  getAdminDashboard: async (): Promise<AdminDashboard> => {
    const { data } = await api.get('/dashboard/admin');
    // Transform backend response to match frontend types
    return {
      platformStats: {
        totalUsers: parseInt(data.platform?.total_carriers || '0') + parseInt(data.platform?.total_brokers || '0') + parseInt(data.platform?.total_shippers || '0'),
        totalCarriers: parseInt(data.platform?.total_carriers || '0'),
        totalBrokers: parseInt(data.platform?.total_brokers || '0'),
        totalShippers: parseInt(data.platform?.total_shippers || '0'),
        totalDataPoints: 0,
        crossValidationRate: data.dataQuality?.overallVerificationRate || 0,
        creditsIssued: 0,
      },
      systemHealth: {
        apiStatus: data.systemHealth?.fmcsaApi?.status === 'healthy' ? 'healthy' : (data.systemHealth?.apiStatus || 'healthy'),
        databaseStatus: data.systemHealth?.database?.status === 'healthy' ? 'connected' : (data.systemHealth?.database?.status || 'connected'),
        cacheHitRate: data.systemHealth?.cacheHitRate || 0.85,
        errorRate: data.systemHealth?.errorRate || 0.001,
      },
      recentAlerts: [],
    };
  },
};

// Broker APIs
export const brokerApi = {
  getCarrierRisk: async (dotNumber: number): Promise<RiskAssessment> => {
    const { data } = await api.get<RiskAssessment>(`/broker/carriers/${dotNumber}/risk`);
    return data;
  },

  reportLoad: async (loadData: {
    carrierDotNumber: number;
    loadNumber: string;
    shipperName: string;
    commodity: string;
    weight: number;
    miles: number;
  }): Promise<{ success: boolean; load: Load }> => {
    const { data } = await api.post<{ success: boolean; load: Load }>('/broker/loads', loadData);
    return data;
  },

  getHighRiskCarriers: async (): Promise<CarrierProfile[]> => {
    const { data } = await api.get<ApiResponse<{ carriers: CarrierProfile[] }>>('/broker/carriers/high-risk');
    return (data as { carriers?: CarrierProfile[] }).carriers || [];
  },

  getLoads: async (): Promise<Load[]> => {
    const { data } = await api.get<ApiResponse<{ loads: Load[] }>>('/broker/loads');
    return (data as { loads?: Load[] }).loads || [];
  },
};

// Equipment-by-plate search result
export interface PlateMatch {
  vin: string;
  make: string | null;
  states: string[];
  inspectionCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  carriers: Array<{ dotNumber: number; legalName: string | null }>;
}

export interface PlateSearchResult {
  plate: string;
  state: string | null;
  matchCount: number;
  matches: PlateMatch[];
}

// VIN APIs
export const vinApi = {
  getVINDetails: async (vin: string): Promise<Vehicle> => {
    const { data } = await api.get<Vehicle>(`/vins/${vin}/details`);
    return data;
  },

  getVINCarriers: async (vin: string): Promise<VinMatch> => {
    const { data } = await api.get<VinMatch>(`/vins/${vin}/carriers`);
    return data;
  },

  getVINLookup: async (vin: string): Promise<Vehicle> => {
    const { data } = await api.get<Vehicle>(`/vins/${vin}/lookup`);
    return data;
  },

  // Find equipment (VIN) by license plate. Plates live per inspected unit, so a
  // plate may map to more than one VIN over time — returns all matches.
  searchByPlate: async (
    plate: string,
    state?: string
  ): Promise<PlateSearchResult> => {
    const response = await api.get<{ data?: PlateSearchResult } & PlateSearchResult>(
      `/vins/by-plate/${encodeURIComponent(plate)}`,
      { params: state ? { state } : undefined }
    );
    const body = response.data as { data?: PlateSearchResult } & PlateSearchResult;
    return body.data ?? body;
  },

  // VIN Timeline with advanced fraud analysis - includes Phase 1/2/3 breakdown
  getVINTimeline: async (vin: string): Promise<Record<string, unknown>> => {
    const response = await api.get<Record<string, unknown>>(`/vins/${vin}/timeline`);
    logger.debug('VIN Timeline response', { vin, dataKeys: Object.keys(response.data || {}) });
    return response.data || {};
  },

  checkShellGameBatch: async (vins: string[]): Promise<VinMatch[]> => {
    const response = await api.post<any>(`/vins/check-shell-game`, { vins });
    // API returns: { success: true, data: { results: [...], totalChecked, shellGamesFound } }
    const results = response.data?.data?.results || response.data?.results || [];
    return Array.isArray(results) ? results : [];
  },

  getInspectionViolations: async (inspectionId: string | number): Promise<Violation[]> => {
    const { data } = await api.get<Violation[]>(`/inspections/${inspectionId}/violations`);
    return Array.isArray(data) ? data : [];
  },
};

// Shell Game Detection APIs (pre-calculated results from background job)
export const shellGameApi = {
  // Get system-wide shell game metrics
  getMetrics: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/shell-game/metrics');
    return data || {};
  },

  // Get all detected shell networks with pagination
  getNetworks: async (params?: {
    limit?: number;
    offset?: number;
    minRiskScore?: number;
    riskLevel?: string;
  }): Promise<PaginatedResponse<Record<string, unknown>>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.minRiskScore) queryParams.append('minRiskScore', params.minRiskScore.toString());
    if (params?.riskLevel) queryParams.append('riskLevel', params.riskLevel);

    const queryString = queryParams.toString();
    const url = queryString ? `/shell-game/networks?${queryString}` : '/shell-game/networks';
    const { data } = await api.get<PaginatedResponse<Record<string, unknown>>>(url);
    return data;
  },

  // Get pre-calculated shell game results for a specific carrier
  getCarrierResults: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/shell-game/carriers/${dotNumber}`);
    return data || {};
  },

  // Get shell game analysis for a specific VIN
  getVINAnalysis: async (vin: string): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/shell-game/vins/${vin}`);
    return data || {};
  },

  // Manually trigger shell game detection job (admin only)
  runDetection: async (): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<{ success: boolean; message: string }>('/shell-game/run');
    return data;
  },
};

// Monitoring APIs
export const monitoringApi = {
  getMonitoredCarriers: async (): Promise<MonitoredCarrier[]> => {
    const { data } = await api.get<ApiResponse<MonitoredCarrier[]>>('/monitoring/carriers');
    return Array.isArray(data) ? data : [];
  },

  addCarrierToWatchlist: async (dotNumber: number, notes?: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<{ success: boolean; message: string }>(`/monitoring/carriers/${dotNumber}`, { notes });
    return data;
  },

  removeCarrierFromWatchlist: async (dotNumber: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.delete<{ success: boolean; message: string }>(`/monitoring/carriers/${dotNumber}`);
    return data;
  },

  getAlerts: async (): Promise<Alert[]> => {
    const { data } = await api.get<ApiResponse<Alert[]>>('/monitoring/alerts');
    return Array.isArray(data) ? data : [];
  },

  markAlertAsRead: async (alertId: number): Promise<{ success: boolean }> => {
    const { data } = await api.put<ApiResponse<{ success: boolean }>>(`/monitoring/alerts/${alertId}/read`);
    return data;
  },

  markAllAlertsAsRead: async (): Promise<{ success: boolean; count: number }> => {
    const { data} = await api.put<{ success: boolean; count: number }>('/monitoring/alerts/read-all');
    return data;
  },
};

// Network Detection APIs
export const networkApi = {
  detectCarrierNetwork: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/networks/carrier/${dotNumber}`);
    return data || {};
  },

  detectAllNetworks: async (params?: {
    minNetworkSize?: number;
    minSharedVins?: number;
    limit?: number;
  }): Promise<Record<string, unknown>[]> => {
    const queryParams = new URLSearchParams();
    if (params?.minNetworkSize) queryParams.append('minNetworkSize', params.minNetworkSize.toString());
    if (params?.minSharedVins) queryParams.append('minSharedVins', params.minSharedVins.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/networks/detect-all?${queryString}` : '/networks/detect-all';
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(url);
    return Array.isArray(data) ? data : [];
  },
};

// Analytics APIs
export const analyticsApi = {
  // Comprehensive carrier analytics (recommended)
  getCarrierAnalytics: async (dotNumber: number, months: number = 24): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/analytics?months=${months}`);
    return data || {};
  },

  // Time-series analytics endpoints
  getViolationsByMonth: async (dotNumber: number, months: number = 12): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/analytics/violations-by-month/${dotNumber}?months=${months}`);
    return Array.isArray(data) ? data : [];
  },

  getCrashesByMonth: async (dotNumber: number, months: number = 12): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/analytics/crashes-by-month/${dotNumber}?months=${months}`);
    return Array.isArray(data) ? data : [];
  },

  getInspectionsByMonth: async (dotNumber: number, months: number = 12): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/analytics/inspections-by-month/${dotNumber}?months=${months}`);
    return Array.isArray(data) ? data : [];
  },

  // Geographic analytics (CEO Agent #4)
  getGeographicAnalytics: async (months: number = 24): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/carriers/0/analytics/geographic?months=${months}`);
    return data || {};
  },

  // Contact & Communication Analytics
  getContactCoverage: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/analytics/contact-coverage');
    return data || {};
  },

  getSharedContacts: async (params?: { limit?: number; minShared?: number }): Promise<Record<string, unknown>[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.minShared) queryParams.append('minShared', params.minShared.toString());
    const queryString = queryParams.toString();
    const url = queryString ? `/analytics/shared-contacts?${queryString}` : '/analytics/shared-contacts';
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(url);
    return Array.isArray(data) ? data : [];
  },

  getAddressClusters: async (params?: { limit?: number; minCarriers?: number }): Promise<Record<string, unknown>[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.minCarriers) queryParams.append('minCarriers', params.minCarriers.toString());
    const queryString = queryParams.toString();
    const url = queryString ? `/analytics/address-clusters?${queryString}` : '/analytics/address-clusters';
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(url);
    return Array.isArray(data) ? data : [];
  },

  getCountyDistribution: async (limit: number = 50): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/analytics/county-distribution?limit=${limit}`);
    return Array.isArray(data) ? data : [];
  },

  // Network & Fraud Analytics (CEO Agent #3)
  getNetworkAnalytics: async (dotNumber?: number): Promise<Record<string, unknown>> => {
    const url = dotNumber
      ? `/analytics/network/${dotNumber}`
      : `/analytics/network/global`;
    const { data } = await api.get<Record<string, unknown>>(url);
    return data || {};
  },

  getContactNetworks: async (dotNumber?: number): Promise<Record<string, unknown>> => {
    const url = dotNumber
      ? `/analytics/network/contact/${dotNumber}`
      : `/analytics/network/contact/all`;
    const { data } = await api.get<Record<string, unknown>>(url);
    return data || {};
  },

  getOfficerNetworks: async (dotNumber?: number): Promise<Record<string, unknown>> => {
    const url = dotNumber
      ? `/analytics/network/officers/${dotNumber}`
      : `/analytics/network/officers/all`;
    const { data } = await api.get<Record<string, unknown>>(url);
    return data || {};
  },

  getVINNetworks: async (dotNumber?: number): Promise<Record<string, unknown>> => {
    const url = dotNumber
      ? `/analytics/network/vins/${dotNumber}`
      : `/analytics/network/vins/all`;
    const { data } = await api.get<Record<string, unknown>>(url);
    return data || {};
  },

  getFraudDetection: async (dotNumber?: number): Promise<Record<string, unknown>> => {
    const url = dotNumber
      ? `/analytics/fraud/${dotNumber}`
      : `/analytics/fraud/dashboard`;
    const { data } = await api.get<Record<string, unknown>>(url);
    return data || {};
  },

  // Network sharing endpoints
  getSharedPhones: async (limit: number = 100): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/analytics/shared-phones?limit=${limit}`);
    return Array.isArray(data) ? data : [];
  },

  getSharedEmails: async (limit: number = 100): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/analytics/shared-emails?limit=${limit}`);
    return Array.isArray(data) ? data : [];
  },

  // Cargo safety analytics
  getCargoSafety: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/analytics/cargo-safety');
    return data || {};
  },

  getCargoNational: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/analytics/cargo-national');
    return data || {};
  },

  getCargoByDot: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/analytics/cargo/${dotNumber}`);
    return data || {};
  },

  // Comprehensive cargo analytics (includes cargo, operations, shippers)
  getCargoAnalytics: async (dotNumber?: number, showNationalData: boolean = false): Promise<Record<string, unknown>> => {
    const url = showNationalData
      ? '/analytics/cargo-national'
      : `/analytics/cargo/${dotNumber || 0}`;
    const { data } = await api.get<Record<string, unknown>>(url);
    return data || {};
  },

  // Fleet analytics
  getFleetAnalytics: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/fleet-analytics`);
    return data || {};
  },

  // Driver analytics
  getDriverAnalytics: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/carriers/${dotNumber}/driver-analytics`);
    return data || {};
  },

  // Benchmarking analytics
  getBenchmarkingData: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/analytics/benchmarking/${dotNumber}`);
    return data || {};
  },

  // Performance scorecard
  getPerformanceScorecard: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/analytics/scorecard/${dotNumber}`);
    return data || {};
  },

  // Predictive analytics
  getPredictiveAnalytics: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/analytics/predictive/${dotNumber}`);
    return data || {};
  },

  // Comprehensive analytics
  getComprehensiveAnalytics: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/analytics/comprehensive/${dotNumber}`);
    return data || {};
  },
};

// Scoring APIs (Health Scores and Predictions - Plan 1)
// This is the CANONICAL API for health scores. Use this instead of projectionApi.getHealthScore().
export const scoringApi = {
  /**
   * Get health score for a carrier - CANONICAL ENDPOINT
   *
   * This is the primary method to get health scores. Replaces the deprecated
   * projectionApi.getHealthScore() method.
   *
   * @param dotNumber - DOT number
   * @param simple - If true (default), return SimpleHealthScore matching frontend calculation.
   *                 If false, return full CompanyHealthScore with detailed components.
   * @param refresh - If true, bypass cache and recalculate
   *
   * @returns SimpleHealthScore with:
   *   - score: 0-100 health score
   *   - riskLevel: 'LOW' | 'MEDIUM' | 'HIGH'
   *   - factors: breakdown of penalties applied
   *   - components: safety, operations, growth, financial scores
   *   - cached: whether result was from cache
   *
   * @example
   * // Get simple health score (recommended)
   * const score = await scoringApi.getHealthScore(123456, true);
   * console.log(score.score, score.riskLevel);
   *
   * // Force refresh from database
   * const freshScore = await scoringApi.getHealthScore(123456, true, true);
   */
  getHealthScore: async (dotNumber: number, simple: boolean = true, refresh: boolean = false): Promise<SimpleHealthScore> => {
    const params = new URLSearchParams();
    if (simple) params.append('simple', 'true');
    if (refresh) params.append('refresh', 'true');
    const queryString = params.toString();
    const url = queryString ? `/scoring/health/${dotNumber}?${queryString}` : `/scoring/health/${dotNumber}`;
    const { data } = await api.get<SimpleHealthScore>(url);
    return data;
  },

  /**
   * Get predictions for a carrier
   * @param dotNumber - DOT number
   * @param options - Forecast options
   */
  getPredictions: async (dotNumber: number, options?: {
    horizon?: number;
    fleetSize?: number;
    companyAge?: number;
    safetyRating?: string;
    oosOrders?: number;
  }): Promise<Record<string, unknown>> => {
    const params = new URLSearchParams();
    if (options?.horizon) params.append('horizon', options.horizon.toString());
    if (options?.fleetSize) params.append('fleetSize', options.fleetSize.toString());
    if (options?.companyAge) params.append('companyAge', options.companyAge.toString());
    if (options?.safetyRating) params.append('safetyRating', options.safetyRating);
    if (options?.oosOrders !== undefined) params.append('oosOrders', options.oosOrders.toString());
    const queryString = params.toString();
    const url = queryString ? `/scoring/predictions/${dotNumber}?${queryString}` : `/scoring/predictions/${dotNumber}`;
    const { data } = await api.get<Record<string, unknown>>(url);
    return data || {};
  },

  /**
   * Force recalculation of health score and predictions
   * @param dotNumber - DOT number
   */
  recalculate: async (dotNumber: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<{ success: boolean; message: string }>(`/scoring/recalculate/${dotNumber}`);
    return data;
  },

  /**
   * Get model accuracy metrics
   * @param model - Model name (default: 'linear_regression')
   * @param window - Time window in days (default: 30)
   */
  getAccuracy: async (model: string = 'linear_regression', window: number = 30): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/scoring/accuracy?model=${model}&window=${window}`);
    return data || {};
  },

  /**
   * Get health score history
   * @param dotNumber - DOT number
   * @param days - Number of days of history (default: 90)
   */
  getHistory: async (dotNumber: number, days: number = 90): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/scoring/history/${dotNumber}?days=${days}`);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get improvement recommendations
   * @param dotNumber - DOT number
   */
  getRecommendations: async (dotNumber: number): Promise<Record<string, unknown>[]> => {
    const { data } = await api.get<ApiResponse<Record<string, unknown>[]>>(`/scoring/recommendations/${dotNumber}`);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get industry benchmarks
   * @param dotNumber - DOT number
   */
  getBenchmarks: async (dotNumber: number): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>(`/scoring/benchmarks/${dotNumber}`);
    return data || {};
  },
};

// Stats APIs (Platform-wide)
export const statsApi = {
  getDashboard: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/stats/dashboard');
    return data || {};
  },

  getOverview: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/stats/overview');
    return data || {};
  },

  getCrashTrends: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/stats/crashes');
    return data || {};
  },

  getInspectionTrends: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/stats/inspections');
    return data || {};
  },

  getViolationTrends: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/stats/violations');
    return data || {};
  },

  getCarrierBreakdown: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/stats/carrier-breakdown');
    return data || {};
  },
};

// Admin APIs (Admin-only endpoints)
export const adminApi = {
  // SMTP Settings
  getSMTPSettings: async (): Promise<{
    isConfigured: boolean;
    settings: {
      host: string;
      port: number;
      user: string;
      passwordSet: boolean;
      fromEmail: string;
      fromName: string;
      secure: boolean;
    };
    lastUpdate: {
      updatedAt: string;
      updatedBy: string;
    } | null;
  }> => {
    const { data } = await api.get<{
      isConfigured: boolean;
      settings: {
        host: string;
        port: number;
        user: string;
        passwordSet: boolean;
        fromEmail: string;
        fromName: string;
        secure: boolean;
      };
      lastUpdate: {
        updatedAt: string;
        updatedBy: string;
      } | null;
    }>('/admin/settings/smtp');
    return data || { isConfigured: false, settings: { host: '', port: 587, user: '', passwordSet: false, fromEmail: '', fromName: '', secure: false }, lastUpdate: null };
  },

  updateSMTPSettings: async (settings: {
    host: string;
    port: number;
    user: string;
    password?: string;
    fromEmail: string;
    fromName?: string;
    secure?: boolean;
  }): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.put<{ success: boolean; message: string }>('/admin/settings/smtp', settings);
    return data;
  },

  testSMTPSettings: async (testEmail: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<{ success: boolean; message: string }>('/admin/settings/smtp/test', { testEmail });
    return data;
  },

  getSMTPAuditLog: async (limit: number = 50, offset: number = 0): Promise<{
    logs: Array<{
      id: number;
      userId: number;
      userEmail: string;
      action: string;
      details: Record<string, unknown>;
      ipAddress: string;
      createdAt: string;
    }>;
  }> => {
    const { data } = await api.get<{
      logs: Array<{
        id: number;
        userId: number;
        userEmail: string;
        action: string;
        details: Record<string, unknown>;
        ipAddress: string;
        createdAt: string;
      }>;
    }>(`/admin/settings/smtp/audit?limit=${limit}&offset=${offset}`);
    return data && data.logs ? data : { logs: [] };
  },

  // Model Metrics
  getModelMetrics: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/admin/model-metrics');
    return data || {};
  },

  // Projection Coverage
  getProjectionCoverage: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/admin/projection-coverage');
    return data || {};
  },

  // Projection Health
  getProjectionHealth: async (): Promise<Record<string, unknown>> => {
    const { data } = await api.get<Record<string, unknown>>('/admin/projection-health');
    return data || {};
  },
};

export default api;
